import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../auth/supabase';
import TenantBrandLockup from '../brand/TenantBrandLockup';
import './prescribed-burns.css';

function isRems(profile) {
  return String(profile?.member_kind || 'line_medic').startsWith('rems_');
}

function burnWhen(burn) {
  const date = burn?.burn_date ? new Date(`${burn.burn_date}T12:00:00`) : null;
  const day = date && !Number.isNaN(date.getTime()) ? date.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) : burn?.burn_date || '';
  const time = String(burn?.start_time || '').slice(0, 5);
  return [day, time].filter(Boolean).join(' · ');
}

function statusLabel(value) {
  return ({
    open_for_signup: 'Open for Signup', signup_closed: 'Signup Closed', roster_selected: 'Roster Finalized', completed: 'Completed', cancelled: 'Cancelled', archived: 'Archived', signed_up: 'Signed Up', withdrawn: 'Withdrawn', selected: 'Selected', standby: 'Standby', not_selected: 'Not Selected', declined: 'Declined', deployed: 'Deployed',
  })[value] || String(value || '').replaceAll('_', ' ');
}

function BurnDetail({ burn, requirements, signup, onChanged, onBack, profile }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!burn?.id || !profile?.id) return;
    supabase.from('prescribed_burn_reads').upsert({
      burn_id: burn.id,
      member_id: profile.id,
      seen_updated_at: burn.updated_at || burn.published_at || new Date().toISOString(),
      read_at: new Date().toISOString(),
    }, { onConflict: 'burn_id,member_id' }).then(() => {});
  }, [burn, profile]);

  const act = async action => {
    setBusy(true); setError('');
    const { error: rpcError } = await supabase.rpc('member_set_prescribed_burn_signup', { target_burn_id: burn.id, action });
    setBusy(false);
    if (rpcError) return setError(rpcError.message || 'Unable to update your signup.');
    await onChanged?.();
  };

  const canSignup = burn.status === 'open_for_signup' && (!signup || ['withdrawn','not_selected'].includes(signup.status));
  const canWithdraw = burn.status === 'open_for_signup' && signup?.status === 'signed_up';
  const canDecline = burn.status === 'roster_selected' && signup?.status === 'selected';
  const canPrepareGear = burn.status === 'roster_selected' && signup?.status === 'selected';
  const readOnly = ['completed','cancelled','archived'].includes(burn.status);

  return (
    <section className="burn-detail">
      <TenantBrandLockup profile={profile} compact />
      <button type="button" className="burn-back" onClick={onBack}>← Prescribed Burns</button>
      <article className="burn-card burn-card-detail">
        <div className="burn-card-top"><span className={`burn-status status-${burn.status}`}>{statusLabel(burn.status)}</span><time>{burnWhen(burn)}</time></div>
        <h2>{burn.name}</h2>
        {burn.location && <p className="burn-location">{burn.location}</p>}
        {requirements.length > 0 && <div className="burn-needs"><strong>Staffing needed</strong><div>{requirements.map(item => <span key={item.id}>{item.quantity} × {item.position}</span>)}</div></div>}
        {burn.staging_instructions && <div className="burn-info"><strong>Meeting / staging</strong><span>{burn.staging_instructions}</span></div>}
        {burn.notes && <div className="burn-info"><strong>Details</strong><span>{burn.notes}</span></div>}

        {signup && <div className={`burn-my-status signup-${signup.status}`}><strong>Your status: {statusLabel(signup.status)}</strong>{signup.selected_position && <span>Assignment: {signup.selected_position}</span>}{signup.status === 'deployed' && <span>This assignment is linked to your My Gear deployment.</span>}</div>}
        {readOnly && <div className="burn-info"><strong>This burn is read-only.</strong><span>The operational record is closed. Attendance changes must be handled by Program Admin.</span></div>}
        {burn.status === 'signup_closed' && <div className="burn-info"><strong>Signup is closed.</strong><span>Program Admin is building the roster. Your existing volunteer status remains on record.</span></div>}

        <div className="burn-actions">
          {canSignup && <button type="button" disabled={busy} className="burn-primary" onClick={() => act('signup')}>{busy ? 'Saving…' : 'Sign Up'}</button>}
          {canWithdraw && <button type="button" disabled={busy} onClick={() => act('withdraw')}>Withdraw Signup</button>}
          {canDecline && <button type="button" disabled={busy} onClick={() => act('decline')}>I Can No Longer Attend</button>}
          {canPrepareGear && <button type="button" className="burn-primary" onClick={() => window.location.assign(`/my-gear?burn=${encodeURIComponent(burn.id)}`)}>Prepare My Gear for This Burn</button>}
          {signup?.status === 'deployed' && !readOnly && <button type="button" onClick={() => window.location.assign('/my-gear')}>Open My Gear</button>}
        </div>
        {error && <div className="burn-error">{error}</div>}
      </article>
    </section>
  );
}

export default function PrescribedBurns({ profile, initialBurnId = '' }) {
  const [burns, setBurns] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [signups, setSignups] = useState([]);
  const [selectedId, setSelectedId] = useState(initialBurnId);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!profile?.id || isRems(profile)) return;
    setError('');
    const [burnResult, requirementResult, signupResult] = await Promise.all([
      // Member View uses a member-scoped RPC so Program Admin privileges do not expose
      // Draft/Archived burns while Greg is intentionally looking at the member experience.
      supabase.rpc('member_list_prescribed_burns'),
      supabase.from('prescribed_burn_requirements').select('*').order('sort_order', { ascending: true }),
      supabase.from('prescribed_burn_signups').select('*').eq('member_id', profile.id),
    ]);
    const firstError = burnResult.error || requirementResult.error || signupResult.error;
    if (firstError) { setError(firstError.message || 'Prescribed Burns are not available yet.'); setStatus('error'); return; }
    setBurns(Array.isArray(burnResult.data) ? burnResult.data : []);
    setRequirements(Array.isArray(requirementResult.data) ? requirementResult.data : []);
    setSignups(Array.isArray(signupResult.data) ? signupResult.data : []);
    setStatus('ready');
  }, [profile]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedId(initialBurnId || ''); }, [initialBurnId]);

  const selected = useMemo(() => burns.find(item => String(item.id) === String(selectedId)) || null, [burns, selectedId]);
  const requirementsFor = burnId => requirements.filter(item => item.burn_id === burnId);
  const signupFor = burnId => signups.find(item => item.burn_id === burnId) || null;

  if (isRems(profile)) return <div className="burn-denied">Prescribed Burns are not available for REMS access.</div>;

  if (selected) {
    return <BurnDetail profile={profile} burn={selected} requirements={requirementsFor(selected.id)} signup={signupFor(selected.id)} onChanged={load} onBack={() => { setSelectedId(''); window.history.replaceState({}, '', '/prescribed-burns'); }} />;
  }

  return (
    <section className="burn-root">
      <TenantBrandLockup profile={profile} compact />
      <div className="burn-heading"><div><span>PROGRAM OPERATIONS</span><h2>Prescribed Burns</h2><p>View upcoming burns, volunteer, and see whether you were selected.</p></div><button type="button" onClick={load}>Refresh</button></div>
      {status === 'loading' && <div className="burn-empty">Loading prescribed burns…</div>}
      {error && <div className="burn-error">{error}</div>}
      {status === 'ready' && !burns.length && <div className="burn-empty">No prescribed burns are posted right now.</div>}
      <div className="burn-list">
        {burns.map(burn => {
          const signup = signupFor(burn.id);
          return <button type="button" className="burn-card" key={burn.id} onClick={() => { setSelectedId(burn.id); window.history.replaceState({}, '', `/prescribed-burns/${burn.id}`); }}><div className="burn-card-top"><span className={`burn-status status-${burn.status}`}>{statusLabel(burn.status)}</span><time>{burnWhen(burn)}</time></div><strong>{burn.name}</strong>{burn.location && <p>{burn.location}</p>}<div className="burn-card-bottom"><span>{requirementsFor(burn.id).map(item => `${item.quantity} ${item.position}`).join(' · ') || 'Staffing details posted inside'}</span>{signup && <b>{statusLabel(signup.status)}</b>}</div></button>;
        })}
      </div>
    </section>
  );
}
